import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { adminCustomers, AdminCustomerListItem } from '../../api/admin';
import { FiUsers, FiEye } from 'react-icons/fi';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  margin-bottom: 16px;
  font-family: Georgia, serif;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Table = styled.table`
  width: 100%;
  background: #F5F0E8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Thead = styled.thead`
  background: #1C1511;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #E8DDD0;
  cursor: pointer;

  &:hover {
    background: #ede8df;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1C1511;
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: #c49015;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: #6B5E4E;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  background: #F5F0E8;
  color: #1C1511;
  border: 1px solid #E8DDD0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;

  &:hover {
    ${props => !props.disabled && `
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    `}
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6B5E4E;
  background: #F5F0E8;
  border-radius: 12px;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;

const PAGE_SIZE = 20;

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const AdminCustomers: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const skip = page * PAGE_SIZE;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['adminCustomers', page],
    queryFn: () => adminCustomers.getAll(skip, PAGE_SIZE),
  });

  const customers: AdminCustomerListItem[] = data?.customers ?? [];
  const total = data?.total ?? 0;

  const handleView = (customer: AdminCustomerListItem) => {
    navigate(`/admin/customers/${customer.user_id}`);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (isError) {
    return (
      <Container>
        <Header>
          <Title>{t('admin.customers.title')}</Title>
        </Header>
        <ErrorMessage>{t('admin.customers.loadError')}</ErrorMessage>
        <ActionButton onClick={() => refetch()}>{t('admin.customers.tryAgainButton')}</ActionButton>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.customers.title')}</Title>
      </Header>

      {customers.length === 0 ? (
        <EmptyState>
          <FiUsers size={48} />
          <h3>{t('admin.customers.emptyTitle')}</h3>
          <p>{t('admin.customers.emptyDescription')}</p>
        </EmptyState>
      ) : (
        <Table>
          <Thead>
            <tr>
              <Th>{t('admin.customers.nameHeader')}</Th>
              <Th>{t('admin.customers.emailHeader')}</Th>
              <Th>{t('admin.customers.phoneHeader')}</Th>
              <Th>{t('admin.customers.pointsBalanceHeader')}</Th>
              <Th data-testid="customers-col-lifetime-discount">{t('admin.customers.lifetimeDiscountHeader')}</Th>
              <Th>{t('admin.customers.ordersHeader')}</Th>
              <Th>{t('admin.customers.joinedHeader')}</Th>
              <Th>{t('admin.customers.actionsHeader')}</Th>
            </tr>
          </Thead>
          <Tbody>
            {customers.map((customer) => (
              <Tr key={customer.user_id} onClick={() => handleView(customer)}>
                <Td>{customer.first_name} {customer.last_name}</Td>
                <Td>{customer.email}</Td>
                <Td>{customer.phone ?? '—'}</Td>
                <Td>{customer.loyalty_points_balance}</Td>
                <Td data-testid={`customer-lifetime-discount-cell-${customer.user_id}`}>{customer.lifetime_discount_percent ?? 0}%</Td>
                <Td>{customer.order_count}</Td>
                <Td>{formatDate(customer.created_at)}</Td>
                <Td>
                  <ActionButton onClick={(e) => { e.stopPropagation(); handleView(customer); }}>
                    <FiEye />
                    {t('admin.customers.viewButton')}
                  </ActionButton>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {total > 0 && (
        <Pagination>
          <PageInfo>
            {t('admin.customers.paginationLabel', { from: skip + 1, to: skip + customers.length, total })}
          </PageInfo>
          <PageButton disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
            {t('admin.customers.previousButton')}
          </PageButton>
          <PageButton
            disabled={skip + customers.length >= total}
            onClick={() => setPage(p => p + 1)}
          >
            {t('admin.customers.nextButton')}
          </PageButton>
        </Pagination>
      )}
    </Container>
  );
};

export default AdminCustomers;
